import React, { useState } from 'react'
import { MetricSourceBadge } from '../../../atoms/MetricSourceBadge'
import {
  LegendContainer,
  LegendHeader,
  ChevronIcon,
  LegendContent,
  LegendColumn,
  ColumnTitle,
  LegendItem,
  AgreementBox,
  AgreementLabel,
  UniqueBox,
  ConflictNote,
  StrikethroughExample,
  DifferenceExample,
  DottedUnderline,
  EmptyDash,
} from './MetricsLegend.styles'

export interface MetricsLegendTranslations {
  title?: string
  sources?: {
    title?: string
    geoportal?: string
    regulation?: string
    algorithm?: string
    /** @deprecated `manual` is the stored form of a user edit; shown as `userEdit` */
    manual?: string
    userEdit?: string
  }
  agreement?: {
    title?: string
    identical?: string
    conflict?: string
    unique?: string
    conflictNote?: string
  }
  difference?: {
    title?: string
    remaining?: string
    exceeded?: string
  }
  authorizedValues?: {
    title?: string
    clickForDetails?: string
    notAvailable?: string
    minLabel?: string
    maxLabel?: string
  }
}

const DEFAULT_LEGEND_TRANSLATIONS: MetricsLegendTranslations = {
  title: 'Legend',
  sources: {
    title: 'Data Sources',
    geoportal: 'Official geoportal',
    regulation: 'Extracted from regulation',
    algorithm: 'Algorithmic estimate',
    manual: 'Manually replaced',
    userEdit: 'Replaced by user',
  },
  agreement: {
    title: 'Source Agreement',
    identical: 'Identical sources',
    conflict: 'Sources in conflict',
    unique: 'Unique source',
    conflictNote: '(strikethrough = non-retained value)',
  },
  difference: {
    title: 'Difference',
    remaining: 'Remaining potential',
    exceeded: 'Exceeded',
  },
  authorizedValues: {
    title: 'Authorized Values',
    clickForDetails: 'Click for details',
    notAvailable: 'Not available',
    minLabel: 'Minimum value',
    maxLabel: 'Maximum value',
  },
}

interface MetricsLegendProps {
  translations?: MetricsLegendTranslations
  defaultOpen?: boolean
}

export const MetricsLegend: React.FC<MetricsLegendProps> = ({
  translations,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const t = {
    ...DEFAULT_LEGEND_TRANSLATIONS,
    ...translations,
    sources: {
      ...DEFAULT_LEGEND_TRANSLATIONS.sources,
      ...translations?.sources,
    },
    agreement: {
      ...DEFAULT_LEGEND_TRANSLATIONS.agreement,
      ...translations?.agreement,
    },
    difference: {
      ...DEFAULT_LEGEND_TRANSLATIONS.difference,
      ...translations?.difference,
    },
    authorizedValues: {
      ...DEFAULT_LEGEND_TRANSLATIONS.authorizedValues,
      ...translations?.authorizedValues,
    },
  }

  return (
    <LegendContainer>
      <LegendHeader onClick={() => setIsOpen(!isOpen)}>
        {t.title}
        <ChevronIcon $isOpen={isOpen}>&#x25BC;</ChevronIcon>
      </LegendHeader>

      {isOpen && (
        <LegendContent>
          {/* Column 1: Data Sources */}
          <LegendColumn>
            <ColumnTitle>{t.sources?.title}</ColumnTitle>

            <LegendItem>
              <MetricSourceBadge source="geoportal" />
              {t.sources?.geoportal}
            </LegendItem>

            <LegendItem>
              <MetricSourceBadge source="llm" />
              {t.sources?.regulation}
            </LegendItem>

            <LegendItem>
              <MetricSourceBadge source="estimate" />
              {t.sources?.algorithm}
            </LegendItem>

            <LegendItem>
              <MetricSourceBadge source="manual" />
              {t.sources?.userEdit}
            </LegendItem>
          </LegendColumn>

          {/* Column 2: Source Agreement */}
          <LegendColumn>
            <ColumnTitle>{t.agreement?.title}</ColumnTitle>

            <AgreementBox $type="identical">
              <MetricSourceBadge source="geoportal" />
              <span style={{ fontWeight: 700, color: '#198754', fontSize: '10px' }}>=</span>
              <MetricSourceBadge source="llm" />
              <AgreementLabel $type="identical">{t.agreement?.identical}</AgreementLabel>
            </AgreementBox>

            <AgreementBox $type="conflict">
              <MetricSourceBadge source="geoportal" />
              <span style={{ fontWeight: 700, color: '#dc3545', fontSize: '10px' }}>&#x2260;</span>
              <MetricSourceBadge source="llm" />
              <StrikethroughExample>5.00</StrikethroughExample>
              <AgreementLabel $type="conflict">{t.agreement?.conflict}</AgreementLabel>
            </AgreementBox>

            <ConflictNote>{t.agreement?.conflictNote}</ConflictNote>

            <UniqueBox>
              <MetricSourceBadge source="geoportal" />
              <span style={{ fontSize: '12px', color: '#495057' }}>{t.agreement?.unique}</span>
            </UniqueBox>
          </LegendColumn>

          {/* Column 3: Authorized Values + Difference */}
          <LegendColumn>
            <ColumnTitle>{t.authorizedValues?.title}</ColumnTitle>

            <LegendItem>
              <DottedUnderline>0.28</DottedUnderline>
              {t.authorizedValues?.clickForDetails}
            </LegendItem>

            <LegendItem>
              <EmptyDash>–</EmptyDash>
              {t.authorizedValues?.notAvailable}
            </LegendItem>

            <LegendItem>
              <DottedUnderline style={{ color: '#0294df' }}>0.50</DottedUnderline>
              {t.authorizedValues?.minLabel}
            </LegendItem>

            <LegendItem>
              <DottedUnderline>0.33</DottedUnderline>
              {t.authorizedValues?.maxLabel}
            </LegendItem>

            <ColumnTitle style={{ marginTop: '6px' }}>{t.difference?.title}</ColumnTitle>

            <LegendItem>
              <DifferenceExample $positive>+0.12</DifferenceExample>
              {t.difference?.remaining}
            </LegendItem>

            <LegendItem>
              <DifferenceExample $positive={false}>-0.12</DifferenceExample>
              {t.difference?.exceeded}
            </LegendItem>
          </LegendColumn>
        </LegendContent>
      )}
    </LegendContainer>
  )
}
